import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./pages/login";
import Layout from "./components/layout/layout";
import { AuthProvider, useAuth } from "./contexts/authContext";
import { Spinner } from "./components/ui/shadcn-io/spinner";
import Dashboard from "./pages/dashboard";
import "./App.css";
import Employees from "./pages/employees";
import AddEmployee from "./pages/addEmployee";
import Salary from "./pages/salary";
import AddEditSalary from "./pages/addSalary";
import Attendance from "./pages/attendance";
import AddEditAttendance from "./pages/addAttendance";
import Payslips from "./pages/payslip";
import Clients from "./pages/clients";
import AddClient from "./pages/addClient";
import ClientDetails from "./pages/clientDetails";
import Projects from "./pages/projects";
import AddProject from "./pages/addProject";
import ProjectDetails from "./pages/projectDetails";
import EmployeeDashboard from "./pages/employeeDashboard";
import EmployeeLeaves from "./pages/employeeLeaves";
import ApplyLeave from "./pages/applyLeave";
import AdminLeaves from "./pages/adminLeaves";
import EmployeeCompOff from "./pages/employeeCompOff";
import ApplyCompOff from "./pages/applyCompOff";
import AdminCompOff from "./pages/adminCompOff";
import EmployeeDocuments from "./pages/employeeDocuments";
import EmployeeSalary from "./pages/employeeSalary";
import EmployeePayslips from "./pages/employeePayslips";
import EmployeeProfile from "./pages/employeeProfile";
import EmployeeAttendance from "./pages/employeeAttendance";
import EmployeeNotes from "./pages/employeeNotes";
import AddNote from "./pages/addNote";
import AdminHolidays from "./pages/adminHolidays";
import EmployeeHolidays from "./pages/employeeHolidays";
import AdminNotes from "./pages/adminNotes";
import AdminNotesDetail from "./pages/adminNotesDetail";
import EmployeeEmployees from "./pages/employeeEmployees";

// Loading Screen Component
function LoadingScreen({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-2xl font-bold text-primary-foreground">
              P
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Payslip Management</h1>
            <p className="text-sm text-muted-foreground">Enterprise Solution</p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3">
          <Spinner variant="ellipsis" size={40} className="text-primary" />
          <div className="text-center">
            <h3 className="text-lg font-medium">{message}</h3>
            <p className="text-sm text-muted-foreground">
              Please wait while we get things ready for you!!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Protected Route Component with role-based access
function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: string[];
}) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role if specified
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    return (
      <Navigate
        to={user.role === "admin" ? "/" : "/employee/dashboard"}
        replace
      />
    );
  }

  return <>{children}</>;
}

// Public Route Component (redirects to dashboard if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  if (isAuthenticated) {
    // Redirect based on role
    return (
      <Navigate
        to={user?.role === "admin" ? "/" : "/employee/dashboard"}
        replace
      />
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes (no sidebar) */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Employee Protected Routes (with same sidebar layout) */}
          <Route
            path="/employee/*"
            element={
              <ProtectedRoute allowedRoles={["employee"]}>
                <Layout>
                  <Routes>
                    <Route path="dashboard" element={<EmployeeDashboard />} />
                    <Route path="profile" element={<EmployeeProfile />} />
                    <Route path="payslips" element={<EmployeePayslips />} />
                    <Route path="leaves" element={<EmployeeLeaves />} />
                    <Route path="leaves/apply" element={<ApplyLeave />} />
                    <Route path="compoff" element={<EmployeeCompOff />} />
                    <Route path="compoff/apply" element={<ApplyCompOff />} />
                    <Route path="attendance" element={<EmployeeAttendance />} />
                    <Route path="salary" element={<EmployeeSalary />} />
                    <Route path="documents" element={<EmployeeDocuments />} />
                    <Route path="notes" element={<EmployeeNotes />} />
                    <Route path="notes/add" element={<AddNote />} />
                    <Route path="holidays" element={<EmployeeHolidays />} />
                    <Route
                      path="team-members"
                      element={<EmployeeEmployees />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="/employee/dashboard" replace />}
                    />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Protected Routes (with sidebar layout) */}
          <Route
            path="/*"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />

                    {/* Employee Management Routes */}
                    <Route path="/employees" element={<Employees />} />
                    <Route path="/employees/add" element={<AddEmployee />} />
                    <Route
                      path="/employees/edit/:id"
                      element={<AddEmployee />}
                    />

                    {/* Salary Routes */}
                    <Route path="/salary" element={<Salary />} />
                    <Route path="/salary/add/:id" element={<AddEditSalary />} />
                    <Route
                      path="/salary/edit/:id"
                      element={<AddEditSalary />}
                    />

                    {/* Attendance Routes */}
                    <Route path="/attendance" element={<Attendance />} />
                    <Route
                      path="/attendance/add/:id"
                      element={<AddEditAttendance />}
                    />
                    <Route
                      path="/attendance/edit/:id"
                      element={<AddEditAttendance />}
                    />

                    {/* Payslip Routes */}
                    <Route path="/payslips" element={<Payslips />} />

                    {/* Client Routes */}
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/clients/add" element={<AddClient />} />
                    <Route path="/clients/edit/:id" element={<AddClient />} />
                    <Route path="/clients/:id" element={<ClientDetails />} />

                    {/* Project Routes */}
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/add" element={<AddProject />} />
                    <Route path="/projects/:id" element={<ProjectDetails />} />
                    <Route path="/projects/edit/:id" element={<AddProject />} />

                    {/* 🆕 Leave Routes */}
                    <Route path="/leaves" element={<AdminLeaves />} />
                    <Route path="/compoff" element={<AdminCompOff />} />
                    <Route path="/holidays" element={<AdminHolidays />} />

                    {/* Notes Routes */}
                    <Route path="/notes" element={<AdminNotes />} />
                    <Route
                      path="/notes/date/:date"
                      element={<AdminNotesDetail />}
                    />

                    {/* Redirect any unmatched admin routes to dashboard */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
